'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import { Combobox } from 'BomdisoftComponents/inputs/combobox/index';
import { PlaceOption } from 'BomdisoftComponents/inputs/location/place-option';
import { usePlacePredictions } from 'BomdisoftComponents/inputs/location/use-place-predictions';
import { LocationDetails, PlaceType } from 'BomdisoftComponents/types';
import { getLanguageFromLocale, noLocationsFoundT } from '@bomdisoft/translations';
import { getGeocodeDataFromAddress } from '@bomdisoft/utilities';
import { useParams } from 'next/navigation';
import { useState } from 'react';

type BulkActionLocationSelectProps = {
  selectHandler: (location: LocationDetails | null) => void;
  placeholder?: string;
  noOptionsText?: string;
};

export const BulkActionLocationSelect = ({ selectHandler, placeholder, noOptionsText }: BulkActionLocationSelectProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const [address, setAddress] = useState<string | null>(null);
  const [placeType, setPlaceType] = useState<PlaceType | null>(null);
  const options = usePlacePredictions(address, placeType?.place_id);

  const handleSelect = async (place: PlaceType | null) => {
    setPlaceType(place);
    if (!place) {
      selectHandler(null);
      return;
    }
    setAddress(place.description);
    try {
      const geocodeData = await getGeocodeDataFromAddress(place.description);
      if (geocodeData)
        selectHandler({
          city: geocodeData.city || place.structured_formatting.main_text,
          country: geocodeData.country || place.structured_formatting.secondary_text,
          country_code: geocodeData.country_code || '--',
          description: place.description,
          latitude: geocodeData.location.latitude,
          longitude: geocodeData.location.longitude,
          place_id: place.place_id,
          postal_code: geocodeData.postcode || '00000',
        });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Combobox
      clearable
      getOptionKey={place => place.place_id}
      getOptionLabel={place => place.description}
      inputValue={address ?? ''}
      noOptionsText={noOptionsText || noLocationsFoundT[language]}
      onInputChange={setAddress}
      onSelect={handleSelect}
      options={options}
      placeholder={placeholder}
      renderOption={place => <PlaceOption place={place} />}
      testId='select-bulk-action-location'
    />
  );
};
